import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Stack,
  Typography,
} from '@mui/material';
import { useRouter } from 'next/router';
import { FormattedMessage } from 'react-intl';

import { Routes } from 'constants/routes.constants';
import { useAfterSignInPath } from 'hooks/useAfterSignInPath';
import { useAccount } from 'queries/accounts/useAccount';
import { useSignOut } from 'queries/accounts/useSignOut';

import Card from 'components/@common/Card';
import StickyButtonContainer from 'components/@saudi-rewards/SaudiRewardsRetroRedeemContainer/StickyButtonContainer';
import { StepData } from 'components/@saudi-rewards/SaudiRewardsRetroRedeemContainer/types';
import { useMultiStep } from 'components/@step-flow/MultiStep';

interface Props {
  type?: 'create' | 'signIn';
  mismatchedEmail?: boolean;
  redeemEmail: string;
}

const TranslationKeyLookup = {
  create: {
    step_1: 'claim.points.info.create.step_1',
    description: 'claim.points.info.create.description',
    purchase_description: 'claim.points.info.create.purchase_description',
    button_label: 'account.create.label',
  },
  signIn: {
    step_1: 'claim.points.info.sign_in.step_1',
    description: 'claim.points.info.sign_in.description',
    purchase_description: 'claim.points.info.sign_in.purchase_description',
    button_label: 'account.sign_in.label',
  },
};

const Info = ({ type = 'create', redeemEmail, mismatchedEmail }: Props) => {
  const multistep = useMultiStep<StepData>();
  const { push } = useRouter();
  const { signOut, isLoading: isLoggingOut } = useSignOut();
  const { setAfterSignInUrl } = useAfterSignInPath();
  const { account } = useAccount();
  const stepsTranslations = [
    TranslationKeyLookup[type].step_1,
    'claim.points.info.step_2',
    'claim.points.info.step_3',
    'claim.points.info.step_4',
  ];

  const handleLogout = () => {
    // Set the after sign in url always
    setAfterSignInUrl(Routes.SaudiRewardsRetroRedeem);
    // Try to keep people on this page when logging out
    signOut({ redirectUrl: Routes.SaudiRewardsRetroRedeem });
  };

  const handleLoginOrSignup = () => {
    // Set the after sign in url always
    setAfterSignInUrl(Routes.SaudiRewardsRetroRedeem);

    push(Routes.Login);
  };

  const handleSubmit = () => {
    if (!!account) {
      multistep.proceed();
    }
  };

  return (
    <Stack sx={{ flexGrow: 1 }}>
      <Typography variant="h6">
        <FormattedMessage id="claim.points.info_title" />
      </Typography>
      <Typography variant="small" sx={{ mb: 3 }}>
        <FormattedMessage id={TranslationKeyLookup[type].description} />
      </Typography>
      <Box flexGrow={{ xs: 1, md: 0 }}>
        <Card
          sx={(theme) => ({
            p: 2,
            border: `1px solid ${theme.vars.palette.grey[800]}`,
            backgroundColor: theme.vars.palette.grey[900],
            flexGrow: 0,
          })}
        >
          <Typography variant="h7" pb={1}>
            <FormattedMessage id="claim.points.info.step_title" />
          </Typography>
          {stepsTranslations.map((stepId) => (
            <Typography key={stepId} variant="body1">
              <FormattedMessage id={stepId} />
            </Typography>
          ))}
        </Card>
      </Box>
      <StickyButtonContainer>
        {(!account || mismatchedEmail) && (
          <Stack flexGrow={0} gap={2} mb={3}>
            <Typography variant="body1">
              <FormattedMessage
                id={TranslationKeyLookup[type].purchase_description}
                values={{ email: <strong>{redeemEmail}</strong> }}
              />
            </Typography>
            {mismatchedEmail && (
              <Alert severity="error" variant="outlined">
                <AlertTitle>
                  <FormattedMessage id="claim.points.info.mismatched_email.title" />
                </AlertTitle>
                <FormattedMessage id="claim.points.info.mismatched_email.description" />
              </Alert>
            )}
          </Stack>
        )}
        {mismatchedEmail ? (
          <Button
            loading={isLoggingOut}
            variant="outlined"
            color="secondary"
            onClick={handleLogout}
            sx={{ width: { xs: '100%', md: 'fit-content' } }}
          >
            <FormattedMessage id="account.sign.out.label" />
          </Button>
        ) : account ? (
          <Button
            onClick={handleSubmit}
            variant="outlined"
            color="secondary"
            sx={{ width: { xs: '100%', md: 'fit-content' } }}
          >
            <FormattedMessage id="event.seating.continue" />
          </Button>
        ) : (
          <Button
            onClick={handleLoginOrSignup}
            variant="outlined"
            color="secondary"
            sx={{ width: { xs: '100%', md: 'fit-content' } }}
          >
            <FormattedMessage id={TranslationKeyLookup[type].button_label} />
          </Button>
        )}
      </StickyButtonContainer>
    </Stack>
  );
};

export default Info;
